import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, ScorePopup, Item, ItemType, ItemMessage, CartItem, GameMode, GameState, LevelCompletedEvent, PersistentCell } from './types';
import {
  GlobalStyle, 
  GameContainer, 
  Grid, 
  Cell as CellComponent, 
  ScorePopupText, 
  GameOverOverlay,
  GameOverPanel,
  GameOverTitle,
  GameOverScore,
  RestartButton,
  ItemsContainer,
  ItemWrapper,
  ItemButton,
  ItemName,
  ItemCount,
  ItemMessageContainer,
  ItemMessageText,
  ItemDescriptionPanel,
  LevelCompleteOverlay,
  LevelCompletePanel,
  LevelCompleteTitle,
  LevelCompleteMessage,
  LevelRewards,
  NextLevelButton,
} from './styles';
import { 
  GRID_SIZE, 
  INITIAL_MOVES, 
  getColorForNumber 
} from './config';
import { getItemsConfig, RECOVER_MOVES_AMOUNT, UPGRADE_AMOUNT, REVIVE_MOVES_AMOUNT } from './itemConfig';
import Shop from './Shop';
import GameRulesModal from './GameRulesModal';
import { INITIAL_DIAMONDS, SHOP_ITEMS_PRICE } from './shopConfig';
import { getLevelById, getNextLevel, LevelGoalType, getLevelName } from './levelConfig';
import GameStatusPanel from './GameStatusPanel';
import storageService, { GamePersistData } from './storageService';
import { isLocalStorageAvailable } from './utils';

const Game: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [grid, setGrid] = useState<Cell[][]>([]);
  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(INITIAL_MOVES);
  const [isAnimating, setIsAnimating] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [scorePopups, setScorePopups] = useState<ScorePopup[]>([]);
  const [gameOver, setGameOver] = useState(false);
  const [items, setItems] = useState<Item[]>(getItemsConfig());
  const [selectedItem, setSelectedItem] = useState<ItemType | null>(null);
  const [selectedCell, setSelectedCell] = useState<{row: number, col: number} | null>(null);
  const [itemMessages, setItemMessages] = useState<ItemMessage[]>([]);
  const animationTimeoutRef = React.useRef<number|null>();
  const [showGameOver, setShowGameOver] = useState(false);
  const [hasReviveCard, setHasReviveCard] = useState(false);
  
  const [diamonds, setDiamonds] = useState(INITIAL_DIAMONDS);
  const [showShop, setShowShop] = useState(false);
  const [isGameRulesModalOpen, setIsGameRulesModalOpen] = useState(false);
  const [gameState, setGameState] = useState<GameState>({
    mode: GameMode.ENDLESS,
    currentLevel: 1,
    totalScore: 0,
    currentScore: 0,
    highestNumber: 0,
    isLevelCompleted: false
  });
  
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelCompleteData, setLevelCompleteData] = useState<LevelCompletedEvent | null>(null);

  const tapSoundRef = useRef<HTMLAudioElement | null>(null);
  const lineSoundRef = useRef<HTMLAudioElement | null>(null);
  const itemSoundRef = useRef<HTMLAudioElement | null>(null);
  const levelCompleteSoundRef = useRef<HTMLAudioElement | null>(null);
  
  // 持久化数据标记 - 用于追踪需要保存的状态变更
  const shouldPersistRef = useRef<boolean>(false);
  
  // 标记需要持久化数据
  const markForPersistence = () => {
    shouldPersistRef.current = true;
  };
  
  // 执行数据持久化
  const persistGameData = () => {
    if (!isLocalStorageAvailable() || !shouldPersistRef.current) return;
    
    try {
      // 清理保存前的网格数据，只保留必要的属性
      const cleanedGrid: PersistentCell[][] = [];
      
      // 确保网格数据结构完整
      for (let i = 0; i < grid.length; i++) {
        const row: PersistentCell[] = [];
        for (let j = 0; j < grid[i].length; j++) {
          row.push({
            value: grid[i][j].value,
            id: `${i}-${j}`,
            row: i,
            col: j
          });
        }
        cleanedGrid.push(row);
      }
      
      const dataToSave: Partial<GamePersistData> = {
        grid: cleanedGrid,
        gameState,
        moves,
        items,
        language: i18n.language,
        diamonds,
        soundOn
      };
      
      // 立即保存，不使用debounce
      storageService.saveData(dataToSave);
      shouldPersistRef.current = false;
    } catch (error) {
      console.error('Game: Error persisting game data:', error);
    }
  };

  // 播放音效的方法
  const playSound = (audioRef: React.RefObject<HTMLAudioElement>) => {
    if (soundOn && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => console.error("播放音效失败:", err));
    }
  };

  // 初始化游戏网格
  const initializeGrid = () => {
    let newGrid: Cell[][] = [];
    let hasMatches = true;
    
    // 持续生成网格直到没有匹配项
    while (hasMatches) {
      newGrid = [];
      for (let i = 0; i < GRID_SIZE; i++) {
        const row: Cell[] = [];
        for (let j = 0; j < GRID_SIZE; j++) {
          const value = Math.floor(Math.random() * 5) + 1;  // 1-5之间的随机数
          row.push({
            value: value,
            color: getColorForNumber(value),
            id: `${i}-${j}`, // 使用简单的行列格式作为 id
            keyId: `${i}-${j}-${Math.random().toString(36).substring(2, 15)}`,
            isNew: true,
            isMatched: false,
            isMovingToTarget: false
          });
        }
        newGrid.push(row);
      }
      
      // 检查整个网格是否有匹配项
      hasMatches = hasMatchesInGrid(newGrid);
    }
    
    // console.log('Game: Grid initialized successfully, size:', newGrid.length, 'x', newGrid[0].length);
    // console.log('Game: Grid values:', newGrid.map(row => row.map(cell => cell.value)));
    return newGrid;
  };
  
  // 检查整个网格是否存在可消除的组合
  const hasMatchesInGrid = (grid: Cell[][]) => {
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        const matches = checkMatches(i, j, grid);
        if (matches.length >= 3) {
          return true;
        }
      }
    }
    return false;
  };

  // 检查连线
  const checkMatches = (row: number, col: number, grid: Cell[][]) => {
    const value = grid[row][col].value;
    const matches: string[] = [];
    
    // 检查水平方向
    let horizontalMatches = [`${row}-${col}`];
    // 向左检查
    for (let i = col - 1; i >= 0 && grid[row][i].value === value; i--) {
      horizontalMatches.push(`${row}-${i}`);
    }
    // 向右检查
    for (let i = col + 1; i < GRID_SIZE && grid[row][i].value === value; i++) {
      horizontalMatches.push(`${row}-${i}`);
    }
    
    if (horizontalMatches.length >= 3) {
      matches.push(...horizontalMatches);
    }

    // 检查垂直方向
    let verticalMatches = [`${row}-${col}`];
    // 向上检查
    for (let i = row - 1; i >= 0 && grid[i][col].value === value; i--) {
      verticalMatches.push(`${i}-${col}`);
    }
    // 向下检查
    for (let i = row + 1; i < GRID_SIZE && grid[i][col].value === value; i++) {
      verticalMatches.push(`${i}-${col}`);
    }
    
    if (verticalMatches.length >= 3) {
      matches.push(...verticalMatches);
    }

    return [...new Set(matches)];
  };

  // 检查是否有复活卡
  const checkReviveCard = () => {
    const reviveItem = items.find(item => item.type === ItemType.REVIVE);
    return Boolean(reviveItem && reviveItem.count > 0);
  };

  // 检查是否有恢复剂
  const checkRecoveryItem = () => {
    const recoveryItem = items.find(item => item.type === ItemType.RECOVER);
    return Boolean(recoveryItem && recoveryItem.count > 0);
  };

  // 处理行动点用完的情况
  const handleMovesDepleted = () => {
    // 先检查是否有恢复剂
    const hasRecovery = checkRecoveryItem();
    if (hasRecovery) {
      // 如果有恢复剂，不显示任何弹窗，等待用户点击时提示
      return;
    }

    // 如果没有恢复剂，检查是否有复活卡
    const hasRevive = checkReviveCard();
    setHasReviveCard(hasRevive);
    if (hasRevive) {
      setShowGameOver(true);
    } else {
      setGameOver(true);
    }
  };

  // 处理点击格子时的行动点检查
  const handleCellClick = (row: number, col: number) => {
    if (isAnimating || gameOver) return;
    
    // 标记数据变更，需要持久化
    markForPersistence();
    
    // 如果有道具被选中，执行道具功能
    if (selectedItem) {
      handleItemUse(selectedItem, row, col);
      return;
    }
    
    // 检查剩余行动点
    if (moves <= 0) {
      // 先检查是否有恢复剂
      const hasRecovery = checkRecoveryItem();
      if (hasRecovery) {
        showItemMessage(t('item.movesDepletedRecovery'));
        return;
      }

      // 如果没有恢复剂，检查是否有复活卡
      if (hasReviveCard) {
        showItemMessage(t('item.movesDepletedRevive'));
      } else {
        showItemMessage(t('item.movesDepletedGameOver'));
        setGameOver(true);
      }
      return;
    }
    
    // 播放点击音效
    playSound(tapSoundRef);

    const newGrid = [...grid];
    const newValue = newGrid[row][col].value + 1;
    
    // 先标记当前点击的格子为递增状态
    newGrid[row][col] = {
      ...newGrid[row][col],
      value: newValue,
      color: getColorForNumber(newValue),
      isIncremented: true
    };

    // 立即更新数字
    setGrid(newGrid);

    // 重置递增状态
    setTimeout(() => {
      if (!newGrid[row][col].isMatched) {  // 如果没有被匹配消除
        setGrid(prev => prev.map((r, ri) => 
          r.map((cell, ci) => 
            ri === row && ci === col 
              ? { ...cell, isIncremented: false }
              : cell
          )
        ));
      }
    }, 300);

    const matches = checkMatches(row, col, newGrid);
    
    if (matches.length >= 3) {
      handleMatches(matches, row, col, newGrid);
      // 立即进行持久化，不等待防抖
      setTimeout(() => persistGameData(), 600);
    } else {
      const newMoves = moves - 1;
      setMoves(newMoves);
      // 检查游戏结束条件
      if (newMoves <= 0) {
        handleMovesDepleted();
      }
      // 立即持久化行动点变更
      persistGameData();
    }
  };

  // 处理匹配消除的逻辑
  const handleMatches = (matches: string[], row: number, col: number, currentGrid: Cell[][]) => {
    // 播放消除音效
    playSound(lineSoundRef);
    
    // 清除之前的动画timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    // 标记匹配的方块，并设置它们的动画目标位置
    const matchedGrid = currentGrid.map(r => r.map(cell => ({
      ...cell,
      isMatched: false,
      isNew: false,
      isIncremented: false,
      isMovingToTarget: false
    })));

    // 标记被点击的格子
    const clickedCellId = `${row}-${col}`;
    
    // 设置其他匹配格子的动画方向（移向点击的格子）
    matches.forEach(match => {
      if (match !== clickedCellId) {
        const [r, c] = match.split('-').map(Number);
        matchedGrid[r][c].isMatched = true;
        matchedGrid[r][c].isMovingToTarget = true;
        matchedGrid[r][c].targetPosition = { row, col };
      }
    });

    // 计算消除得分 - 修改为数字总和而不是格子数量
    let scoreValue = 0;
    let maxNumberInMatch = 0;
    matches.forEach(match => {
      const [r, c] = match.split('-').map(Number);
      scoreValue += currentGrid[r][c].value;
      
      if (currentGrid[r][c].value > maxNumberInMatch) {
        maxNumberInMatch = currentGrid[r][c].value;
      }
    });
    
    // 更新最高消除数字
    const updatedHighestNumber = Math.max(maxNumberInMatch, gameState.highestNumber || 0);

    setGrid(matchedGrid);
    
    // 获取点击的Cell元素位置来添加分数弹出效果
    const cellElement = document.getElementById(`cell-${row}-${col}`);
    if (cellElement) {
      const rect = cellElement.getBoundingClientRect();
      const popupId = `score-${Date.now()}`;
      
      // 添加新的分数弹出
      setScorePopups(prev => [...prev, {
        id: popupId,
        score: scoreValue,
        x: rect.left + rect.width / 2,
        y: rect.top
      }]);
      
      // 移除动画完成后的分数弹出
      setTimeout(() => {
        setScorePopups(prev => prev.filter(popup => popup.id !== popupId));
      }, 1000);
    }
    
    // 设置一个timeout来等待动画结束
    animationTimeoutRef.current = window.setTimeout(() => {
      // 创建更新后的网格，移除匹配的格子，保留点击的格子但值加1
      const updatedGrid = matchedGrid.map(r => r.map(cell => ({
        ...cell,
        isMatched: false,
        isNew: false,
        isIncremented: false,
        isMovingToTarget: false
      })));
      
      // 计算点击格子的新值（基于原始值加1）
      const clickedValue = currentGrid[row][col].value + 1;

      // 处理所有匹配的格子
      matches.forEach(match => {
        const [r, c] = match.split('-').map(Number);
        
        if (match !== clickedCellId) {
          // 非点击格子：生成新随机值
          const newValue = Math.floor(Math.random() * 5) + 1;  // 1-5之间的随机数
          updatedGrid[r][c] = {
            value: newValue,
            color: getColorForNumber(newValue),
            id: `${r}-${c}`, // 使用简单的行列格式作为 id
            keyId: `${r}-${c}-${Date.now()}`,
            isNew: true,
            isMatched: false,
            isIncremented: false,
            isMovingToTarget: false
          };
        } else {
          // 点击格子：值加1
          updatedGrid[r][c] = {
            ...updatedGrid[r][c],
            value: clickedValue,
            color: getColorForNumber(clickedValue),
            isIncremented: true,
            isMovingToTarget: false
          };
        }
      });

      setGrid(updatedGrid);
      
      const newScore = score + scoreValue;
      setScore(newScore);
      
      setGameState(prevState => ({
        ...prevState,
        totalScore: prevState.totalScore + scoreValue,
        currentScore: prevState.currentScore + scoreValue,
        highestNumber: updatedHighestNumber
      }));
      
      setMoves(prev => prev + 1);
      // 标记数据变更，需要持久化
      markForPersistence();
      
      // 重置新方块的动画状态，并检查新生成的方块是否形成新的匹配
      checkLevelCompletion(gameState.currentScore + scoreValue, updatedHighestNumber);

      setTimeout(() => {
        const finalGrid = updatedGrid.map(row => row.map(cell => ({
          ...cell,
          isNew: cell.isNew, // 保留新方块的动画
          isMatched: false,
          isIncremented: false,
          isMovingToTarget: false
        })));
        
        setGrid(finalGrid);
        // 标记数据变更，需要持久化
        markForPersistence();
        
        // 延迟一点时间再检查新的匹配，让用户看清结果
        setTimeout(() => {
          // 检查新生成的方块是否形成新的匹配
          checkForCascadingMatches(finalGrid);
        }, 500);
      }, 300);
    }, 400); // 减少动画时间，原来是600ms
  };

  // 检查级联匹配
  const checkForCascadingMatches = (currentGrid: Cell[][]) => {
    let hasFoundMatch = false;
    let matchPosition = { row: -1, col: -1 };
    let bestMatches: string[] = [];
    
    // 检查整个网格中的所有可能匹配
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        const matches = checkMatches(i, j, currentGrid);
        if (matches.length >= 3) {
          // 找到最大的匹配组合
          if (matches.length > bestMatches.length) {
            bestMatches = matches;
            matchPosition = { row: i, col: j };
          }
          hasFoundMatch = true;
        }
      }
    }
    
    // 如果找到匹配，处理它
    if (hasFoundMatch && bestMatches.length >= 3) {
      handleMatches(bestMatches, matchPosition.row, matchPosition.col, currentGrid);
    }
  };

  // 处理声音开关切换
  const toggleSound = () => {
    setSoundOn(prev => !prev);
    markForPersistence();
  };
  
  // 重新开始游戏
  const restartGame = () => {
    setGrid(initializeGrid());
    setScore(0);
    setMoves(INITIAL_MOVES);
    setGameOver(false);
    setShowGameOver(false);
    setScorePopups([]);
    setItems(getItemsConfig());
    setSelectedItem(null);
    setSelectedCell(null);
    setItemMessages([]);
    
    setGameState(prevState => ({
      ...prevState,
      currentScore: 0,
      highestNumber: 0,
      isLevelCompleted: false
    }));
    
    // 标记数据变更，需要持久化
    markForPersistence();
  };

  // 清除所有存储的游戏数据并重置游戏
  const clearGameData = () => {
    if (isLocalStorageAvailable()) {
      storageService.clearAll();
      showItemMessage(t('item.dataCleared'));
    }
    
    restartGame();
  };

  // 处理道具选择
  const handleItemSelect = (itemType: ItemType) => {
    // 如果游戏已结束，不进行操作
    if (gameOver) return;
    
    // 标记数据变更，需要持久化
    markForPersistence();
    
    // 检查道具是否可用（数量 > 0）
    const itemIndex = items.findIndex(item => item.type === itemType);
    if (itemIndex === -1 || items[itemIndex].count <= 0) return;
    
    // 如果当前已选择此道具，则取消选择
    if (selectedItem === itemType) {
      setSelectedItem(null);
      setSelectedCell(null);
      return;
    }
    
    // 播放道具选择音效
    playSound(itemSoundRef);
    
    // 如果是行动恢复剂或复活卡，可以直接使用
    if (itemType === ItemType.RECOVER) {
      handleItemUse(itemType);
      return; // 直接返回，不设置selectedItem
    } else if (itemType === ItemType.REVIVE) {
      // 检查复活卡使用条件
      if (moves > 0) {
        showItemMessage(t('item.reviveCardUsage'));
        return; // 不选择道具
      }
      handleItemUse(itemType);
      return; // 直接返回，不设置selectedItem
    }
    
    // 选择需要点击格子才能使用的道具
    setSelectedItem(itemType);
    setSelectedCell(null);
  };
  
  // 处理道具使用
  const handleItemUse = (itemType: ItemType, row?: number, col?: number) => {
    // 查找道具
    const itemIndex = items.findIndex(item => item.type === itemType);
    if (itemIndex === -1 || items[itemIndex].count <= 0) return;
    
    // 播放道具使用音效
    playSound(itemSoundRef);
    
    // 根据道具类型执行不同操作
    switch (itemType) {
      case ItemType.RESET: 
        if (row === undefined || col === undefined) return;
        handleResetItem(row, col);
        break;
      case ItemType.SWAP:
        if (typeof row === 'number' && typeof col === 'number') {
          // 第一次选择格子时不消耗道具
          if (!selectedCell) {
            setSelectedCell({ row, col });
            return;
          } else {
            // 第二次选择格子，执行交换
            handleSwapItem(selectedCell.row, selectedCell.col, row, col);
          }
        }
        break;
      case ItemType.UPGRADE:
        if (row === undefined || col === undefined) return;
        handleUpgradeItem(row, col);
        break;
      case ItemType.RECOVER:
        handleRecoverItem();
        break;
      case ItemType.REVIVE:
        handleReviveItem();
        break;
    }
    
    // 减少道具数量
    const updatedItems = [...items];
    updatedItems[itemIndex].count -= 1;
    setItems(updatedItems);
    
    // 重置选择状态
    setSelectedItem(null);
    setSelectedCell(null);
  };
  
  // 重置器道具功能实现
  const handleResetItem = (row: number, col: number) => {
    // 创建新的随机值
    const newValue = Math.floor(Math.random() * 5) + 1;
    
    // 更新网格
    const newGrid = [...grid];
    newGrid[row][col] = {
      ...newGrid[row][col],
      value: newValue,
      color: getColorForNumber(newValue),
      isNew: true,
      id: `${row}-${col}`, // 使用简单的行列格式作为 id
      keyId: `${row}-${col}-${Date.now()}`
    };
    
    setGrid(newGrid);
    // 标记数据变更，需要持久化
    markForPersistence();
    
    // 创建道具使用消息
    showItemMessage(t('item.reset'));
  };
  
  // 交换器道具功能实现
  const handleSwapItem = (row1: number, col1: number, row2: number, col2: number) => {
    // 检查是否为相邻格子
    const isAdjacent = 
      (Math.abs(row1 - row2) === 1 && col1 === col2) || 
      (Math.abs(col1 - col2) === 1 && row1 === row2);
    
    if (!isAdjacent) {
      showItemMessage(t('item.swapInvalid'));
      return;
    }
    
    // 交换两个格子的值
    const newGrid = [...grid];
    const temp = { ...newGrid[row1][col1] };
    
    newGrid[row1][col1] = {
      ...newGrid[row2][col2],
      id: `${row1}-${col1}` // 使用简单的行列格式作为 id
    };
    
    newGrid[row2][col2] = {
      ...temp,
      id: `${row2}-${col2}`, // 使用简单的行列格式作为 id
      keyId: `${row2}-${col2}-${Date.now()}`,
    };
    
    setGrid(newGrid);
    // 标记数据变更，需要持久化
    markForPersistence();
    
    // 创建道具使用消息
    showItemMessage(t('item.swap'));

    // 延迟一点时间后检查是否有新的匹配
    setTimeout(() => {
      // 检查交换后的两个位置是否有匹配
      const matches1 = checkMatches(row1, col1, newGrid);
      const matches2 = checkMatches(row2, col2, newGrid);
      
      // 如果任一位置有匹配，处理匹配
      if (matches1.length >= 3) {
        handleMatches(matches1, row1, col1, newGrid);
      } else if (matches2.length >= 3) {
        handleMatches(matches2, row2, col2, newGrid);
      }
    }, 300); // 等待300ms让交换动画完成
  };
  
  // 升级器道具功能实现
  const handleUpgradeItem = (row: number, col: number) => {
    // 将格子的值提升2点
    const newGrid = [...grid];
    const newValue = newGrid[row][col].value + UPGRADE_AMOUNT;
    
    newGrid[row][col] = {
      ...newGrid[row][col],
      value: newValue,
      color: getColorForNumber(newValue),
      isIncremented: true
    };
    
    setGrid(newGrid);
    // 标记数据变更，需要持久化
    markForPersistence();
    
    // 更新最高消除数字（如果新值更高）
    const updatedHighestNumber = Math.max(newValue, gameState.highestNumber || 0);
    if (updatedHighestNumber > gameState.highestNumber) {
      setGameState(prevState => ({
        ...prevState,
        highestNumber: updatedHighestNumber
      }));
    }
    
    // 重置递增状态
    setTimeout(() => {
      setGrid(prev => prev.map((r, ri) => 
        r.map((cell, ci) => 
          ri === row && ci === col 
            ? { ...cell, isIncremented: false }
            : cell
        )
      ));
    }, 300);
    
    // 创建道具使用消息
    showItemMessage(t('item.upgrade', { amount: UPGRADE_AMOUNT }));
  };
  
  // 行动恢复剂道具功能实现
  const handleRecoverItem = () => {
    // 恢复3点行动值
    setMoves(prev => prev + RECOVER_MOVES_AMOUNT);
    // 标记数据变更，需要持久化
    markForPersistence();
    
    // 创建道具使用消息
    showItemMessage(t('item.recovered', { amount: RECOVER_MOVES_AMOUNT }));
  };
  
  // 复活卡道具功能实现
  const handleReviveItem = () => {
    // 检查使用条件
    if (moves > 0) {
      showItemMessage(t('item.reviveCardUsage'));
      return;
    }

    // 恢复行动点
    setMoves(REVIVE_MOVES_AMOUNT);
    // 标记数据变更，需要持久化
    markForPersistence();
    
    // 创建道具使用消息
    showItemMessage(t('item.revived', { amount: REVIVE_MOVES_AMOUNT }));

    // 添加复活动画效果
    const newGrid = grid.map(row => 
      row.map(cell => ({
        ...cell,
        isReviving: true
      }))
    );
    setGrid(newGrid);
    markForPersistence();
    
    // 3秒后移除复活动画效果
    setTimeout(() => {
      setGrid(prev => prev.map(row => 
        row.map(cell => ({
          ...cell,
          isReviving: false
        }))
      ));
      // 动画结束后再次标记持久化
      markForPersistence();
    }, 3000);
  };

  // 显示道具使用消息
  const showItemMessage = (message: string) => {
    const newMessage: ItemMessage = {
      id: `message-${Date.now()}`,
      message,
      timestamp: Date.now()
    };
    
    setItemMessages(prev => [...prev, newMessage]);
    
    // 3秒后移除消息
    setTimeout(() => {
      setItemMessages(prev => prev.filter(msg => msg.id !== newMessage.id));
    }, 3000);
  };

  // 使用复活卡
  const useReviveCard = () => {
    const reviveItemIndex = items.findIndex(item => item.type === ItemType.REVIVE);
    if (reviveItemIndex !== -1 && items[reviveItemIndex].count > 0) {
      handleReviveItem();
      
      // 减少复活卡数量
      const updatedItems = [...items];
      updatedItems[reviveItemIndex].count -= 1;
      setItems(updatedItems);
      
      setShowGameOver(false);
    } else {
      showItemMessage(t('item.reviveCardInsufficient'));
    }
  };

  // 关闭游戏结束弹窗
  const closeGameOver = () => {
    setShowGameOver(false);
    setGameOver(true);
  };

  const handlePurchase = (purchasedItems: CartItem[]) => {
    let totalPrice = 0;
    
    const updatedItems = [...items];
    
    purchasedItems.forEach(({ itemType, quantity }) => {
      const itemIndex = updatedItems.findIndex(item => item.type === itemType);
      if (itemIndex !== -1) {
        updatedItems[itemIndex] = {
          ...updatedItems[itemIndex],
          count: updatedItems[itemIndex].count + quantity
        };
        
        const item = SHOP_ITEMS_PRICE.find(item => item.itemType === itemType);
        if (item) {
          totalPrice += item.price * quantity;
        }
      }
    });
    
    setItems(updatedItems);
    
    setDiamonds(prev => prev - totalPrice);
    
    playSound(itemSoundRef);
    
    setShowShop(false);
    
    showItemMessage(t('item.purchaseSuccess', { count: purchasedItems.length }));
    
    // 标记数据变更，需要持久化
    markForPersistence();
  };
  
  const handleRecharge = () => {
    showItemMessage(t('item.rechargeNotImplemented'));
    setDiamonds(prev => prev + 20);
    
    // 标记数据变更，需要持久化
    markForPersistence();
  };
  
  const toggleShop = () => {
    if (gameOver) return;
    setShowShop(prev => !prev);
  };

  const toggleGameMode = () => {
    setGameState(prevState => ({
      ...prevState,
      mode: prevState.mode === GameMode.ENDLESS ? GameMode.LEVEL : GameMode.ENDLESS,
      currentLevel: prevState.mode === GameMode.ENDLESS ? 1 : undefined,
      isLevelCompleted: false
    }));
    
    restartGame();
    
    // 标记数据变更，需要持久化
    markForPersistence();
  };
  
  
  const checkLevelCompletion = (currentScore: number, highestNumber: number) => {
    if (gameState.mode !== GameMode.LEVEL || gameState.isLevelCompleted) {
      return false;
    }
    
    const currentLevel = getLevelById(gameState.currentLevel || 1);
    if (!currentLevel) return false;
    
    const { goal } = currentLevel;
    
    let isCompleted = false;
    
    switch (goal.type) {
      case LevelGoalType.SCORE:
        isCompleted = currentScore >= goal.targetValue;
        break;
      case LevelGoalType.MAX_NUMBER:
        isCompleted = highestNumber >= goal.targetValue;
        break;
      default:
        isCompleted = false;
    }
    
    if (isCompleted && !gameState.isLevelCompleted) {
      handleLevelComplete(currentLevel.id);
    }
    
    return isCompleted;
  };
  
  const handleLevelComplete = (levelId: number) => {
    const level = getLevelById(levelId);
    if (!level) return;
    
    playSound(levelCompleteSoundRef);
    
    const eventData: LevelCompletedEvent = {
      levelId: level.id,
      score: gameState.currentScore,
      moves: moves,
      reward: level.reward
    };
    
    setGameState(prevState => ({
      ...prevState,
      isLevelCompleted: true
    }));
    
    if (level.reward?.diamonds) {
      setDiamonds(prevDiamonds => prevDiamonds + level.reward!.diamonds!);
    }
    
    if (level.reward?.items) {
      const updatedItems = [...items];
      level.reward.items.forEach(rewardItem => {
        const itemIndex = updatedItems.findIndex(item => item.type === rewardItem.type);
        if (itemIndex !== -1) {
          updatedItems[itemIndex].count += rewardItem.count;
        }
      });
      setItems(updatedItems);
    }
    
    setLevelCompleteData(eventData);
    setShowLevelComplete(true);
  };
  
  const goToNextLevel = () => {
    const currentLevelId = gameState.currentLevel || 1;
    const nextLevel = getNextLevel(currentLevelId);
    
    if (nextLevel) {
      setGameState(prevState => ({
        ...prevState,
        currentLevel: nextLevel.id,
        isLevelCompleted: false,
        currentScore: 0
      }));
      
      setGrid(initializeGrid());
      setScore(0);
      setMoves(INITIAL_MOVES);
      setGameOver(false);
      setShowGameOver(false);
      setScorePopups([]);
      setSelectedItem(null);
      setSelectedCell(null);
      setItemMessages([]);
    } else {
      showItemMessage(t('item.congratulationsAllLevelsCompleted'));
      setGameState(prevState => ({
        ...prevState,
        mode: GameMode.ENDLESS
      }));
    }
    
    setShowLevelComplete(false);
    
    // 标记数据变更，需要持久化
    markForPersistence();
  };

  // 加载保存的游戏数据
  const loadSavedGameData = () => {
    if (!isLocalStorageAvailable()) return false;
    
    try {
      const savedData = storageService.loadData();
      let success = false;
      
      
      if (Object.keys(savedData).length === 0) return false;
      
      // 加载网格数据
      if (savedData.grid && savedData.grid.length > 0 && 
          savedData.grid.length === GRID_SIZE && 
          savedData.grid[0].length === GRID_SIZE) {
          
        // 验证网格数据结构完整性
        const isValidGrid = savedData.grid.every((row, rowIndex) => 
          row.length === GRID_SIZE && 
          row.every((cell, colIndex) => 
            typeof cell === 'object' && 
            cell !== null && 
            typeof cell.value === 'number' && 
            typeof cell.id === 'string'
          )
        );
        
        if (isValidGrid) {
          // 确保网格中的每个Cell对象都有正确的属性
          const restoredGrid = savedData.grid.map((row, rowIndex) => 
            row.map((cell, colIndex) => ({
              value: cell.value,
              color: getColorForNumber(cell.value), // 确保颜色与数值匹配
              id: `${rowIndex}-${colIndex}`, // 使用简单格式的 id
              keyId: `${rowIndex}-${colIndex}-${Date.now()}`,
              isNew: false,
              isMatched: false,
              isMovingToTarget: false,
              isIncremented: false,
              isReviving: false
            }))
          );
          
          setGrid(restoredGrid);
          success = true;
        } else {
          console.error('Invalid grid data structure');
          // 只有在网格数据结构无效时才初始化新网格
          setGrid(initializeGrid());
        }
      } else {
        // 只有在缺少网格数据时才初始化新网格
        setGrid(initializeGrid());
      }
      
      // 加载游戏状态
      if (savedData.gameState && typeof savedData.gameState === 'object' && 
          'mode' in savedData.gameState && 
          'totalScore' in savedData.gameState && 
          'currentScore' in savedData.gameState && 
          'highestNumber' in savedData.gameState) {
        setGameState(savedData.gameState);
        success = true;
      }
      
      // 加载行动点
      if (typeof savedData.moves === 'number' && !isNaN(savedData.moves)) {
        setMoves(savedData.moves);
        success = true;
      }
      
      // 加载道具
      if (savedData.items && Array.isArray(savedData.items) && savedData.items.length > 0) {
        setItems(savedData.items);
        success = true;
      } else {
        setItems(getItemsConfig());
      }
      
      // 加载钻石
      if (typeof savedData.diamonds === 'number' && !isNaN(savedData.diamonds)) {
        setDiamonds(savedData.diamonds);
        success = true;
      }
      
      // 加载声音设置
      if (typeof savedData.soundOn === 'boolean') {
        setSoundOn(savedData.soundOn);
        success = true;
      }
      
      // 加载语言设置
      if (savedData.language && typeof savedData.language === 'string' && 
          (savedData.language === 'zh' || savedData.language === 'en')) {
        if (savedData.language !== i18n.language) {
          i18n.changeLanguage(savedData.language);
        }
        success = true;
      }
      
      return success;
    } catch (error) {
      console.error('加载游戏数据失败:', error);
      // 如果加载失败，初始化新游戏
      setGrid(initializeGrid());
      return false;
    }
  };

  // 监听语言变化，更新道具配置
  useEffect(() => {
    const handleLanguageChanged = () => {
      setItems(getItemsConfig());
      // 标记数据变更，需要持久化
      markForPersistence();
    };
    
    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  // 初始化游戏数据
  useEffect(() => {
    // 尝试加载保存的游戏数据
    const hasLoadedData = loadSavedGameData();
    
    // 如果没有加载到数据，初始化新的游戏
    if (!hasLoadedData) {
      const newGrid = initializeGrid();
      setGrid(newGrid);
      
      // 初始化游戏后立即保存初始状态（延迟确保状态已更新）
      setTimeout(() => {
        markForPersistence();
        // 绕过debounce直接保存
        try {
          const cleanedGrid: PersistentCell[][] = [];
          
          for (let i = 0; i < newGrid.length; i++) {
            const row: PersistentCell[] = [];
            for (let j = 0; j < newGrid[i].length; j++) {
              row.push({
                value: newGrid[i][j].value,
                id: `${i}-${j}`,
                keyId: `${i}-${j}-${Date.now()}`,
                row: i,
                col: j
              });
            }
            cleanedGrid.push(row);
          }
          
          const initialData: Partial<GamePersistData> = {
            grid: cleanedGrid,
            gameState,
            moves,
            items,
            language: i18n.language,
            diamonds,
            soundOn
          };
          // 直接调用saveData而不是persistData，跳过防抖
          storageService.saveData(initialData);
        } catch (err) {
          console.error('Game: Error saving initial state:', err);
        }
      }, 500);
    }
    
    // 添加beforeunload事件，确保页面关闭前保存数据
    const handleBeforeUnload = () => {
      if (shouldPersistRef.current) {
        persistGameData();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);
  
  // 监听数据变化并持久化
  useEffect(() => {
    // 创建防抖持久化函数
    const debouncedPersistData = () => {
      if (shouldPersistRef.current) {
        persistGameData();
      }
    };
    
    // 设置定时器，防抖保存
    const timerId = setTimeout(debouncedPersistData, 1000);
    
    return () => {
      clearTimeout(timerId);
    };
  }, [grid, gameState, moves, items, diamonds, soundOn, i18n.language]);

  return (
    <>
      <GlobalStyle />
      <GameContainer>
        
        {/* 使用游戏状态面板组件 */}
        <GameStatusPanel 
          gameState={gameState} 
          moves={moves} 
          diamonds={diamonds}
          onToggleSound={toggleSound}
          onToggleShop={toggleShop}
          onToggleGameMode={toggleGameMode}
          soundOn={soundOn}
          onToggleGameRules={setIsGameRulesModalOpen}
          onClearData={clearGameData}
        />
        
        {/* 道具消息显示 */}
        <ItemMessageContainer>
          {itemMessages.map(message => (
            <ItemMessageText key={message.id}>
              {message.message}
            </ItemMessageText>
          ))}
        </ItemMessageContainer>
        
        {/* 道具栏 */}
        <ItemsContainer>
          {items.map(item => (
            <ItemWrapper 
              key={item.type} 
              $isActive={item.count > 0 && !gameOver}
              onClick={() => item.count > 0 && !gameOver && handleItemSelect(item.type)}
            >
              <ItemButton $isSelected={selectedItem === item.type}>
                {item.icon}
                <ItemName>{item.name}</ItemName>
              </ItemButton>
              {item.count > 0 && <ItemCount>{item.count}</ItemCount>}
              <ItemDescriptionPanel>{item.description}</ItemDescriptionPanel>
            </ItemWrapper>
          ))}
        </ItemsContainer>
        
        <Grid>
          {grid.map((row, rowIndex) => 
            row.map((cell, colIndex) => (
              <CellComponent
                key={cell.keyId}
                id={`cell-${rowIndex}-${colIndex}`}
                color={cell.color}
                $isMatched={cell.isMatched}
                $isNew={cell.isNew}
                $isIncremented={cell.isIncremented}
                $isMovingToTarget={cell.isMovingToTarget}
                $isReviving={cell.isReviving || false}
                $targetRow={cell.targetPosition?.row}
                $targetCol={cell.targetPosition?.col}
                $isSelected={Boolean(
                  selectedItem === ItemType.SWAP && 
                  selectedCell &&
                  selectedCell.row === rowIndex && 
                  selectedCell.col === colIndex
                )}
                onClick={() => handleCellClick(rowIndex, colIndex)}
                style={{
                  '--row': rowIndex,
                  '--col': colIndex,
                } as React.CSSProperties}
              >
                {cell.value}
              </CellComponent>
            ))
          )}
        </Grid>
        
        {/* 分数弹出元素 */}
        {scorePopups.map((popup) => (
          <ScorePopupText 
            key={popup.id} 
            style={{ 
              left: `${popup.x}px`, 
              top: `${popup.y}px` 
            }}
          >
            +{popup.score}
          </ScorePopupText>
        ))}
        
        {/* 音频元素 */}
        <audio ref={tapSoundRef} src="/sounds/tap.mp3" preload="auto" />
        <audio ref={lineSoundRef} src="/sounds/line.mp3" preload="auto" />
        <audio ref={itemSoundRef} src="/sounds/item.mp3" preload="auto" />
        <audio ref={levelCompleteSoundRef} src="/sounds/level-complete.mp3" preload="auto" />
        
        {/* 游戏结束弹窗 */}
        {showGameOver && (
          <GameOverOverlay>
            <GameOverPanel>
              <GameOverTitle>{t('gameOver.title')}</GameOverTitle>
              <GameOverScore>{t('gameOver.currentScore')}: {gameState.currentScore}</GameOverScore>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <RestartButton onClick={useReviveCard}>
                  {t('gameOver.useReviveCard')}
                </RestartButton>
                <RestartButton onClick={closeGameOver} style={{ backgroundColor: '#666' }}>
                  {t('gameOver.close')}
                </RestartButton>
              </div>
            </GameOverPanel>
          </GameOverOverlay>
        )}

        {/* 最终游戏结束弹窗 */}
        {gameOver && !showGameOver && (
          <GameOverOverlay>
            <GameOverPanel>
              <GameOverTitle>{t('gameOver.finalTitle')}</GameOverTitle>
              <GameOverScore>{t('gameOver.finalScore')}: {gameState.currentScore}</GameOverScore>
              <GameOverScore>{t('gameOver.totalScore')}: {gameState.totalScore}</GameOverScore>
              <RestartButton onClick={restartGame}>{t('gameOver.restartGame')}</RestartButton>
            </GameOverPanel>
          </GameOverOverlay>
        )}
        
        {showLevelComplete && levelCompleteData && (
          <LevelCompleteOverlay>
            <LevelCompletePanel>
              <LevelCompleteTitle>{t('levelComplete.congratulations')}</LevelCompleteTitle>
              <LevelCompleteMessage>
                {t('levelComplete.completedLevel', { 
                  levelName: (() => {
                    const level = getLevelById(gameState.currentLevel || 1);
                    return level ? getLevelName(level) : t('game.unknownLevel');
                  })()
                })}
              </LevelCompleteMessage>
              
              <LevelRewards>
                <div>{t('levelComplete.score')}: {gameState.currentScore}</div>
                <div>{t('levelComplete.moves')}: {moves}</div>
                {levelCompleteData.reward?.diamonds && (
                  <div>{t('levelComplete.diamondsEarned')}: +{levelCompleteData.reward.diamonds}</div>
                )}
                {levelCompleteData.reward?.items && levelCompleteData.reward.items.length > 0 && (
                  <div>{t('levelComplete.itemsEarned')}: {levelCompleteData.reward.items.map(item => 
                    `${item.type} x${item.count}`).join(', ')}</div>
                )}
              </LevelRewards>
              
              <NextLevelButton onClick={goToNextLevel}>
                {t('levelComplete.nextLevel')}
              </NextLevelButton>
            </LevelCompletePanel>
          </LevelCompleteOverlay>
        )}
        {/* 商店弹窗 */}
        <Shop 
          isOpen={showShop}
          onClose={() => setShowShop(false)}
          items={items}
          diamonds={diamonds}
          onPurchase={handlePurchase}
          onRecharge={handleRecharge}
        />
        {/* 游戏规则弹窗 */}
        <GameRulesModal
          isOpen={isGameRulesModalOpen}
          onClose={() => setIsGameRulesModalOpen(false)}
        />
      </GameContainer>
    </>
  );
};

export default Game;